import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  FaClock,
  FaTrash,
  FaFileExcel,
  FaReceipt,
  FaSearch,
  FaFilter,
  FaSpinner,
  FaFileInvoiceDollar,
  FaEllipsisV,
  FaPlus,
  FaEye,
  FaMoneyBillWave,
  FaUndoAlt,
  FaFileUpload,
  FaChevronLeft,
  FaChevronRight,
  FaCalendarAlt,
} from "react-icons/fa";

import apiRequest from "../services/api";
import InvoiceModal from "../components/InvoiceCreate";
import InvoicePaymentModal from "../components/InvoicePaymentModal";
import RegisterWithholds from "../components/RegisterWithholds";
import InvoiceReceipt from "../components/InvoiceReceipt";
import WithholdingReceipt from "../components/WithholdingReceipt";
import AuditLogModal from "../components/AuditLogModal";
import InvoiceCancelModal from "../components/CancelModal";
import { exportInvoicesToExcel } from "../utils/exportInvoicesToExcel";
import PosOpenModal from "../components/PosOpenModal";
import PosCloseModal from "../components/PosCloseModal";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "DRAFT", label: "Draft" },
  { value: "PAID", label: "Paid" },
  { value: "PARTIALLY_PAID", label: "Partially Paid" },
  { value: "DELIVERED", label: "Delivered" },
  { value: "RECEIVED", label: "Received" },
  { value: "RETURNED", label: "Returned" },
  { value: "PARTIALLY_RETURNED", label: "Partially Returned" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "COMPLETED", label: "Completed" },
];

const InvoicesPage = ({ invoiceType }) => {
  const [invoices, setInvoices] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [selectedType, setSelectedType] = useState(invoiceType || "SALE");

  // Optional filters
  const [status, setStatus] = useState("");
  const today = new Date();
  const currentYear = today.getFullYear();
  const [filterStartDate, setFilterStartDate] = useState(
    `${currentYear}-01-01`
  );

  const [filterEndDate, setFilterEndDate] = useState(
    `${currentYear}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
      today.getDate()
    ).padStart(2, "0")}`
  );

  const [openAudit, setOpenAudit] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState(null);

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(15);
  const [totalPages, setTotalPages] = useState(0);
  const PAGE_SIZE_OPTIONS = [15, 30, 50, 100, 1000, 2000, 5000];
  const [totalElements, setTotalElements] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const [openReceipt, setOpenReceipt] = useState(false);
  const [openWhReceipt, setOpenWhReceipt] = useState(false);
  const [openRegisterWithholds, setOpenRegisterWithholds] = useState(false);

  const [activeInvoice, setActiveInvoice] = useState(null);
  const [receiptInvoice, setReceiptInvoice] = useState(null);
  const [receiptWithholdingType, setReceiptWithholdingType] = useState(null);

  const [showExternalImportModal, setShowExternalImportModal] = useState(false);
  const [importMode, setImportMode] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const type = invoiceType?.toUpperCase();
  const refundNote = "Memo";
  /////////cash register
  const [activeShift, setActiveShift] = useState(null);
  const [isCheckingShift, setIsCheckingShift] = useState(true);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [openingCash, setOpeningCash] = useState("");
  const [countedCash, setCountedCash] = useState("");
  const [terminalId] = useState("POS-01");
  const [isSubmittingShift, setIsSubmittingShift] = useState(false);
  const [shiftError, setShiftError] = useState("");

  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [notice, setNotice] = useState(null);
  const [hasLink, setHasLink] = useState(false);

  useEffect(() => {
    const parsed = JSON.parse(localStorage.getItem("user") || "{}");
    const userData = parsed?.data || {};
    setHasLink(userData.tenant?.externalSource || false);
  }, []);

  useEffect(() => {
    checkActiveShiftStatus();
  }, []);

  const checkActiveShiftStatus = async () => {
    try {
      setIsCheckingShift(true);
      setShiftError("");
      const res = await apiRequest("pos/active");
      if (res?.data || res) {
        setActiveShift(res.data || res);
        setShowOpenModal(false);
      } else {
        setShowOpenModal(true);
      }
    } catch {
      setActiveShift(null);
      setShowOpenModal(true);
    } finally {
      setIsCheckingShift(false);
    }
  };

  const handleOpenShift = async (e) => {
    e.preventDefault();
    const cashVal = parseFloat(openingCash);
    if (isNaN(cashVal) || cashVal < 0) {
      setShiftError("Please enter a valid starting cash amount.");
      return;
    }
    try {
      setIsSubmittingShift(true);
      setShiftError("");
      const res = await apiRequest(
        `pos/open?terminalId=${terminalId}&openingCash=${cashVal}`,
        { method: "POST" }
      );
      setActiveShift(res.data || res);
      setShowOpenModal(false);
      setOpeningCash("");
      setNotice({
        type: "success",
        message: "Register Drawer shift opened successfully.",
      });
    } catch (err) {
      setShiftError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to open register shift."
      );
    } finally {
      setIsSubmittingShift(false);
    }
  };

  const handleCloseShift = async (e) => {
    e.preventDefault();
    const countVal = parseFloat(countedCash);
    if (isNaN(countVal) || countVal < 0) {
      setShiftError("Please enter a valid total counted physical cash.");
      return;
    }
    try {
      setIsSubmittingShift(true);
      setShiftError("");
      const res = await apiRequest(`pos/close?countedCash=${countVal}`, {
        method: "POST",
      });
      const summary = res.data || res;
      setActiveShift(null);
      setShowCloseModal(false);
      setCountedCash("");
      alert(
        `Shift Terminated Successfully (Z-Report)!\n\n` +
          `Expected Cash in Drawer: ${etb(summary.expectedCash || 0)}\n` +
          `Actual Counted Cash: ${etb(summary.countedCash || 0)}\n` +
          `Variance Calculation: ${etb(summary.difference || 0)}`
      );
      setShowOpenModal(true);
    } catch (err) {
      setShiftError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to close register shift."
      );
    } finally {
      setIsSubmittingShift(false);
    }
  };

  //
  const [modals, setModals] = useState({
    create: false,
    process: false,
    payment: false,
    cancel: false,
    memo: false,
    withhold: false,
  });

  const [activeMenuId, setActiveMenuId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState({ top: 0, left: 0 });
  const menuContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  ////sorting
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedInvoices = useMemo(() => {
    if (!invoices?.length) return [];

    const sorted = [...invoices].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle dates
      if (["createdAt", "invoiceDate"].includes(sortConfig.key)) {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      // Handle numbers
      if (
        [
          "totalAmount",
          "paidAmount",
          "withhold",
          "vatWithhold",
          "balanceAmount",
        ].includes(sortConfig.key)
      ) {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }

      // Handle strings
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = (bValue || "").toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [invoices, sortConfig]);

  const SortableHeader = ({ label, sortKey, className = "" }) => {
    const isActive = sortConfig.key === sortKey;
    const direction = isActive ? sortConfig.direction : null;

    return (
      <th
        className={`px-4 py-4 cursor-pointer select-none hover:text-slate-800 dark:hover:text-white transition ${className}`}
        onClick={() => handleSort(sortKey)}
      >
        <div className="flex items-center gap-1.5">
          <span>{label}</span>
          <span className="text-[10px] opacity-70">
            {direction === "asc" ? "↑" : direction === "desc" ? "↓" : "↕"}
          </span>
        </div>
      </th>
    );
  };

  useEffect(() => {
    if (invoiceType) setSelectedType(invoiceType);
  }, [invoiceType]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        event.target.closest(".ellipsis-trigger-btn") ||
        (menuContainerRef.current &&
          menuContainerRef.current.contains(event.target))
      ) {
        return;
      }
      setActiveMenuId(null);
    };

    if (activeMenuId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeMenuId]);

  // ─── Fetch invoices (paginated + filters) ─────────────────────────────────
  const fetchInvoicesList = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();

      if (keyword.trim()) params.append("keyword", keyword.trim());
      if (selectedType) params.append("type", selectedType);
      if (status) params.append("status", status);

      // API expects LocalDateTime → send full day range
      if (filterStartDate) {
        params.append("startDate", `${filterStartDate}T00:00:00`);
      }
      if (filterEndDate) {
        params.append("endDate", `${filterEndDate}T23:59:59`);
      }

      params.append("page", page.toString());
      params.append("size", size.toString());

      const response = await apiRequest(`invoices?${params.toString()}`);
      const content = response?.data?.content || [];

      setInvoices(content);
      setTotalPages(response?.data?.totalPages || 0);
      setTotalElements(response?.data?.totalElements || 0);
    } catch (err) {
      console.error("Failed to fetch invoices:", err);
      setInvoices([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    keyword,
    selectedType,
    status,
    filterStartDate,
    filterEndDate,
    page,
    size,
  ]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [keyword, selectedType, status, filterStartDate, filterEndDate, size]);

  useEffect(() => {
    fetchInvoicesList();
  }, [fetchInvoicesList]);

  const handleOpenActionModal = (invoice, modalType, extra = {}) => {
    setActiveInvoice(invoice);
    setActiveMenuId(null);
    setModals((prev) => ({ ...prev, [modalType]: true, ...extra }));
  };

  const closeModal = (modalType) => {
    setModals((prev) => ({ ...prev, [modalType]: false }));
  };

  const refreshRegistryGrid = () => {
    fetchInvoicesList();
  };

  const clearFilters = () => {
    setKeyword("");
    setStatus("");
    setFilterStartDate("");
    setFilterEndDate("");
    setPage(0);
  };

  // ─── Import Handlers ──────────────────────────────────────────────────────
  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const params = new URLSearchParams();
      if (selectedType) params.append("type", selectedType);

      const url = params.toString()
        ? `invoices/import?${params.toString()}`
        : "invoices/import";

      await apiRequest(url, { method: "POST", body: formData });
      setPage(0);
      fetchInvoicesList();
    } catch (err) {
      alert(
        err?.message ||
          "Failed to import invoices. Please check the file format."
      );
    } finally {
      setIsImporting(false);
    }
  };

  const formatDate = (date) => {
    const [year, month, day] = date.split("-");
    return `${month}/${day}/${year}`;
  };

  const handleExternalSync = async () => {
    if (!startDate || !endDate) return;
    setIsImporting(true);
    try {
      const issueDate = `${formatDate(startDate)} - ${formatDate(endDate)}`;
      const params = new URLSearchParams();
      params.append("type", selectedType);
      params.append("importMode", importMode.trim());
      params.append("issueDate", issueDate);

      await apiRequest(`invoices/import-external?${params.toString()}`, {
        method: "POST",
      });

      setShowExternalImportModal(false);
      setImportMode("CASH");
      setStartDate("");
      setEndDate("");
      setPage(0);
      fetchInvoicesList();
    } catch (err) {
      console.error("External import failed:", err);
      alert(err?.message || "Failed to import external invoices.");
    } finally {
      setIsImporting(false);
    }
  };

  const getStatusBadgeStyles = (s) => {
    switch (s?.toUpperCase()) {
      case "PAID":
        return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20";
      case "DELIVERED":
        return "bg-sky-50 text-sky-700 border-sky-200 dark:bg-orange-500/10 dark:text-sky-400 dark:border-green-500/20";
      case "RECEIVED":
        return "bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-orange-500/10 dark:text-sky-400 dark:border-cyan-500/20";
      case "COMPLETED":
        return "bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-500/10 dark:text-teal-400 dark:border-teal-500/20";
      case "PARTIALLY_PAID":
        return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20";
      case "PARTIALLY_DELIVERED":
        return "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-orange-500/10 dark:text-indigo-400 dark:border-indigo-500/20";
      case "PARTIALLY_RECEIVED":
        return "bg-sky-50 text-sky-700 border-sky-200 dark:bg-orange-500/10 dark:text-sky-400 dark:border-sky-500/20";
      case "CONFIRMED":
        return "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/20";
      case "CANCELLED":
        return "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20";
      case "DRAFT":
        return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-orange-500/10 dark:text-amber-400 dark:border-amber-500/20";
      default:
        return "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-500/10 dark:text-slate-400 dark:border-slate-500/20";
    }
  };

  const getLogisticalWorkflowDetails = (invoice) => {
    if (!invoice) {
      return {
        canPay: false,
        canReturn: false,
        canCancel: false,
        canWithhold: false,
      };
    }

    const s = (invoice.status || "").toUpperCase();
    const balance = Number(invoice.balanceAmount || 0);
    const isBlockedStatus = s === "DRAFT" || s === "CANCELLED";
    const isFullyPaid = s === "PAID";

    const canPay =
      !isBlockedStatus &&
      !isFullyPaid &&
      balance > 0 &&
      !["PAID", "PARTIALLY_RETURNED", "RETURNED"].includes(s);

    const canCancel = s === "DELIVERED" || s === "RECEIVED";
    const canWithhold =
      !activeInvoice?.whRegistered &&
      ["DELIVERED", "RECEIVED", "PAID", "PARTIALLY_PAID"].includes(s);

    const canReturn = ["PAID", "PARTIALLY_PAID", "PARTIALLY_RETURNED"].includes(
      s
    );

    return { canPay, canReturn, canCancel, canWithhold };
  };

  const workflow = getLogisticalWorkflowDetails(activeInvoice);

  const canOpenReceipt =
    ["SALE", "PURCHASE", "SALE_RETURN", "PURCHASE_RETURN"].includes(
      activeInvoice?.type?.toUpperCase()
    ) && !["DRAFT", "CANCELLED"].includes(activeInvoice?.status?.toUpperCase());

  const menuButtonClass =
    "w-full text-left px-3 py-2 rounded-lg text-xs transition flex items-center gap-2 font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10";

  const formatMoney = (n) =>
    `${Number(n || 0).toLocaleString("en-ET", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ETB`;

  const pageTotals = useMemo(() => {
    return invoices.reduce(
      (acc, inv) => {
        if (
          ["CANCELLED", "RETURNED"].includes((inv.status || "").toUpperCase())
        )
          return acc;
        acc.subtotal += Number(inv.subtotal + inv.nonTaxableAmount || 0);
        acc.discount += Number(inv.discount || 0);
        acc.excise += Number(inv.excise + inv.nonTaxableExcise || 0);
        acc.tax += Number(inv.tax || 0);
        acc.totalAmount += Number(inv.totalAmount || 0);
        acc.paidAmount += Number(inv.paidAmount || 0);
        acc.withhold += Number(inv.withhold || 0);
        acc.vatWithhold += Number(inv.vatWithhold || 0);
        acc.balanceAmount += Number(inv.balanceAmount || 0);
        acc.count += 1;
        return acc;
      },
      {
        subtotal: 0,
        discount: 0,
        excise: 0,
        tax: 0,
        totalAmount: 0,
        paidAmount: 0,
        withhold: 0,
        vatWithhold: 0,
        balanceAmount: 0,
        count: 0,
      }
    );
  }, [invoices]);

  const handleExport = () => {
    exportInvoicesToExcel(invoices, invoiceType);
  };

  const hasActiveFilters =
    keyword.trim() || status || filterStartDate || filterEndDate;

  // ─── UI ───────────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-full p-4 md:p-8 font-sans
      bg-slate-50 text-slate-800 selection:bg-amber-500/20 selection:text-slate-900
      dark:bg-slate-950 dark:text-slate-100 dark:selection:bg-amber-500/30 dark:selection:text-white"
    >
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div
          className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl shadow-sm border backdrop-blur-md
  bg-white border-slate-200
  dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl"
        >
          <div className="flex items-center gap-3.5">
            <div
              className="p-3 rounded-xl shadow-inner border
      bg-gradient-to-br from-amber-100 to-orange-50 border-amber-200 text-amber-600
      dark:from-amber-500/20 dark:to-orange-500/10 dark:border-amber-500/30 dark:text-amber-400"
            >
              <FaFileInvoiceDollar className="text-2xl" />
            </div>
            <div>
              <h1 className="text-xl font-black tracking-tight uppercase text-slate-900 dark:text-white">
                {selectedType === "SALE" ? "Sales Ledger" : "Purchase Ledger"}
              </h1>
              <p className="text-xs mt-0.5 text-slate-500 dark:text-slate-400">
                <span className="font-mono font-semibold text-amber-600 dark:text-amber-400">
                  {selectedType}
                </span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelected}
              className="hidden"
            />

            {/* New Invoice */}
            <button
              type="button"
              onClick={() => handleOpenActionModal(null, "create")}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-600 hover:bg-amber-500 text-white font-bold rounded-xl text-xs transition shadow-lg shadow-amber-600/10"
            >
              <FaPlus /> New {selectedType === "SALE" ? "Sale" : "Purchase"}
            </button>

            {/* Import */}
            {hasLink && (
              <button
                type="button"
                onClick={() => setShowExternalImportModal(true)}
                disabled={isImporting}
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition border
        bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300
        dark:bg-slate-800/60 dark:border-white/10 dark:text-slate-200 dark:hover:bg-slate-700/60
        disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isImporting ? (
                  <>
                    <FaSpinner className="animate-spin text-sm" />
                    Importing...
                  </>
                ) : (
                  <>
                    <FaFileUpload className="text-sm" />
                    Import
                  </>
                )}
              </button>
            )}

            {/* Export */}
            <button
              type="button"
              onClick={handleExport}
              disabled={!invoices.length}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition border
        bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300
        dark:bg-slate-800/60 dark:border-white/10 dark:text-slate-200 dark:hover:bg-slate-700/60
        disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <FaFileExcel className="text-sm" />
              Export
            </button>

            {activeShift && (
              <button
                type="button"
                onClick={() => {
                  setShiftError("");
                  setShowCloseModal(true);
                }}
                className="flex items-center gap-2 text-xs px-3.5 py-2 rounded-xl font-semibold border transition
      bg-gradient-to-r from-red-500/10 to-orange-500/10
      text-red-600 border-red-300/60 hover:from-red-500/20 hover:to-orange-500/20
      dark:from-red-600/20 dark:to-orange-600/10
      dark:text-red-400 dark:border-red-500/40 dark:hover:from-red-600/30"
              >
                <FaClock className="text-sm" />
                <span>Close Register</span>
              </button>
            )}
          </div>
        </div>

        {/* ========== FILTERS ========== */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 p-3 rounded-xl border backdrop-blur-sm
          bg-white border-slate-200
          dark:bg-slate-900/20 dark:border-white/5"
        >
          {/* Keyword */}
          <div className="relative col-span-1 sm:col-span-2">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 dark:text-slate-500">
              <FaSearch className="text-xs" />
            </span>
            <input
              type="text"
              placeholder="Search invoice #, IRN, partner..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="w-full rounded-xl pl-9 pr-4 py-2.5 text-xs transition focus:outline-none focus:border-amber-500/40
                bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400
                dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
            />
          </div>

          {/* Status */}
          <div className="relative">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-xl px-3 py-2.5 text-xs transition appearance-none font-medium focus:outline-none focus:border-amber-500/40
                bg-slate-50 border border-slate-200 text-slate-800
                dark:bg-slate-950/60 dark:border-white/10 dark:text-white"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Start date */}
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 dark:text-slate-500">
              <FaCalendarAlt className="text-[10px]" />
            </span>
            <input
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              className="w-full rounded-xl pl-8 pr-3 py-2.5 text-xs transition focus:outline-none focus:border-amber-500/40
                bg-slate-50 border border-slate-200 text-slate-800
                dark:bg-slate-950/60 dark:border-white/10 dark:text-white"
              title="Invoice date from"
            />
          </div>

          {/* End date */}
          <div className="relative flex gap-2">
            <input
              type="date"
              value={filterEndDate}
              min={filterStartDate || undefined}
              onChange={(e) => setFilterEndDate(e.target.value)}
              className="w-full rounded-xl px-3 py-2.5 text-xs transition focus:outline-none focus:border-amber-500/40
                bg-slate-50 border border-slate-200 text-slate-800
                dark:bg-slate-950/60 dark:border-white/10 dark:text-white"
              title="Invoice date to"
            />
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="shrink-0 px-3 rounded-xl text-xs font-semibold border
                  bg-white border-slate-200 text-slate-600 hover:bg-slate-50
                  dark:bg-slate-800 dark:border-white/10 dark:text-slate-300 dark:hover:bg-slate-700"
                title="Clear filters"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div
          className="border rounded-2xl shadow-sm backdrop-blur-md overflow-hidden
          bg-white border-slate-200
          dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[900px]">
              <thead>
                <tr
                  className="border-b text-[10px] font-bold tracking-wider uppercase
    bg-slate-50 border-slate-200 text-slate-500
    dark:bg-slate-950/50 dark:border-white/10 dark:text-slate-400"
                >
                  <SortableHeader label="CreatedAt" sortKey="createdAt" />
                  <SortableHeader
                    label="Invoice Date"
                    sortKey="invoiceDate"
                    className="px-5"
                  />
                  <SortableHeader
                    label="Reference"
                    sortKey="invoiceNumber"
                    className="px-5"
                  />
                  <SortableHeader
                    label="Client"
                    sortKey="partnerName"
                    className="px-5"
                  />
                  <SortableHeader label="Status" sortKey="status" />
                  <SortableHeader
                    label="Total"
                    sortKey="totalAmount"
                    className="text-right"
                  />
                  <SortableHeader
                    label="Paid"
                    sortKey="paidAmount"
                    className="text-right"
                  />
                  <SortableHeader
                    label="TWHT"
                    sortKey="withhold"
                    className="text-right"
                  />
                  <SortableHeader
                    label="VWHT"
                    sortKey="vatWithhold"
                    className="text-right"
                  />
                  <SortableHeader
                    label="Balance"
                    sortKey="balanceAmount"
                    className="text-right"
                  />
                  <SortableHeader label="CreatedBy" sortKey="salesPerson" />
                  <th className="px-5 py-4 text-center w-24">Actions</th>
                </tr>
              </thead>
              <tbody
                className="divide-y font-mono text-xs
                divide-slate-100 text-slate-600
                dark:divide-white/5 dark:text-slate-300"
              >
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="text-center py-12 text-slate-500 font-sans italic dark:text-slate-500"
                    >
                      <div className="flex items-center justify-center gap-3">
                        <FaSpinner className="animate-spin text-amber-500 text-base" />
                        <span>Loading ledger data...</span>
                      </div>
                    </td>
                  </tr>
                ) : invoices.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="text-center py-12 text-slate-500 font-sans italic dark:text-slate-500"
                    >
                      No invoices found.
                    </td>
                  </tr>
                ) : (
                  sortedInvoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="group transition relative hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                    >
                      <td className="px-4 py-3.5 font-sans">
                        <span
                          className="font-medium text-xs px-2 py-1 rounded-lg border
                          bg-slate-100 text-slate-600 border-slate-200
                          dark:bg-white/5 dark:text-slate-400 dark:border-white/5"
                        >
                          {invoice.createdAt
                            ? new Date(invoice.createdAt).toLocaleDateString(
                                "en-US"
                              )
                            : ""}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 font-sans font-bold tracking-wide text-slate-900 dark:text-white">
                        {invoice.invoiceDate
                          ? new Date(invoice.invoiceDate).toLocaleDateString(
                              "en-US"
                            )
                          : ""}
                      </td>
                      <td className="px-5 py-3.5 font-sans font-bold tracking-wide text-slate-900 dark:text-white">
                        <div className="text-amber-600 dark:text-amber-500 font-medium max-w-xs truncate">
                          {invoice.invoiceNumber}
                        </div>
                        {invoice.irn && (
                          <div className="text-[10px] font-normal text-slate-500 flex items-start gap-1 mt-0.5 min-w-0">
                            <FaReceipt className="text-[9px] flex-shrink-0 mt-0.5" />
                            <span className="min-w-0 break-all">
                              {invoice.irn}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3.5 font-sans font-bold tracking-wide text-slate-900 dark:text-white">
                        {invoice.partnerName}
                      </td>
                      <td className="px-4 py-3.5 font-sans">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase border ${getStatusBadgeStyles(
                            invoice.status
                          )}`}
                        >
                          {invoice.status}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-right font-semibold text-slate-700 dark:text-slate-200">
                        {(invoice.totalAmount || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3.5 text-right font-medium text-emerald-600 dark:text-emerald-400">
                        {(invoice.paidAmount || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3.5 text-right font-medium text-amber-600 dark:text-amber-500">
                        {(invoice.withhold || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3.5 text-right font-medium text-amber-600 dark:text-amber-500">
                        {(invoice.vatWithhold || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3.5 text-right font-medium text-amber-600 dark:text-amber-500">
                        {(invoice.balanceAmount || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3.5 font-sans">
                        <span
                          className="font-medium text-xs px-2 py-1 rounded-lg border
                          bg-slate-100 text-slate-600 border-slate-200
                          dark:bg-white/5 dark:text-slate-400 dark:border-white/5"
                        >
                          {invoice.salesPerson}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-center font-sans">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (activeMenuId === invoice.id) {
                              setActiveMenuId(null);
                            } else {
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              setMenuAnchor({
                                top: rect.bottom + window.scrollY + 6,
                                left: rect.left + window.scrollX - 156,
                              });
                              setActiveInvoice(invoice);
                              setActiveMenuId(invoice.id);
                            }
                          }}
                          className="ellipsis-trigger-btn p-2 rounded-xl border transition
                            bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-500 hover:text-slate-800
                            dark:bg-white/5 dark:hover:bg-white/10 dark:border-white/5 dark:text-slate-400 dark:hover:text-white"
                        >
                          <FaEllipsisV className="text-xs pointer-events-none" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              {/* Summary row – current page */}
              {!isLoading && invoices.length > 0 && (
                <tfoot>
                  {/* Header of summary */}
                  <tr
                    className="border-t-2 text-[10px] font-bold uppercase tracking-wider
      bg-slate-100 border-slate-300 text-slate-500
      dark:bg-slate-950/80 dark:border-white/20 dark:text-slate-400"
                  >
                    <td colSpan={10} className="px-5 py-2.5 font-sans">
                      Page summary
                      <span className="ml-2 font-mono font-normal normal-case text-slate-400">
                        ({pageTotals.count} active · cancelled & draft excluded)
                      </span>
                    </td>
                  </tr>

                  {/* Total amount */}
                  <tr
                    className="text-xs font-semibold
      bg-slate-50 border-t border-slate-100 text-slate-800
      dark:bg-slate-950/40 dark:border-white/5 dark:text-slate-100"
                  >
                    <td colSpan={4} className="px-5 py-2.5 font-sans">
                      Subtotal
                    </td>
                    <td
                      className="px-4 py-2.5 text-right font-mono"
                      colSpan={1}
                    >
                      {formatMoney(pageTotals.subtotal)}
                    </td>
                    <td colSpan={5} />
                  </tr>

                  {/* Tax */}
                  {pageTotals.tax > 0 && (
                    <tr
                      className="text-xs font-semibold
      bg-slate-50 border-t border-slate-200 text-slate-700
      dark:bg-slate-950/50 dark:border-white/5 dark:text-slate-200"
                    >
                      <td colSpan={4} className="px-5 py-2.5 font-sans">
                        Tax
                      </td>
                      <td
                        className="px-4 py-2.5 text-right font-mono"
                        colSpan={1}
                      >
                        {formatMoney(pageTotals.tax)}
                      </td>
                      <td colSpan={5} />
                    </tr>
                  )}
                  {/* discount */}
                  {pageTotals.discount > 0 && (
                    <tr
                      className="text-xs font-semibold
      bg-slate-50 border-t border-slate-200 text-slate-700
      dark:bg-slate-950/50 dark:border-white/5 dark:text-slate-200"
                    >
                      <td colSpan={4} className="px-5 py-2.5 font-sans">
                        Discount
                      </td>
                      <td
                        className="px-4 py-2.5 text-right font-mono"
                        colSpan={1}
                      >
                        {formatMoney(pageTotals.discount)}
                      </td>
                      <td colSpan={5} />
                    </tr>
                  )}
                  {/* excise */}
                  {pageTotals.excise > 0 && (
                    <tr
                      className="text-xs font-semibold
      bg-slate-50 border-t border-slate-200 text-slate-700
      dark:bg-slate-950/50 dark:border-white/5 dark:text-slate-200"
                    >
                      <td colSpan={4} className="px-5 py-2.5 font-sans">
                        Excise
                      </td>
                      <td
                        className="px-4 py-2.5 text-right font-mono"
                        colSpan={1}
                      >
                        {formatMoney(pageTotals.excise)}
                      </td>
                      <td colSpan={5} />
                    </tr>
                  )}

                  {/* Total amount */}
                  <tr
                    className="text-xs font-semibold
      bg-slate-50 border-t border-slate-100 text-slate-800
      dark:bg-slate-950/40 dark:border-white/5 dark:text-slate-100"
                  >
                    <td colSpan={4} className="px-5 py-2.5 font-sans">
                      Total amount
                    </td>
                    <td
                      className="px-4 py-2.5 text-right font-mono"
                      colSpan={1}
                    >
                      {formatMoney(pageTotals.totalAmount)}
                    </td>
                    <td colSpan={5} />
                  </tr>

                  {/* Paid */}
                  <tr
                    className="text-xs font-semibold
      bg-slate-50 border-t border-slate-100 text-emerald-700
      dark:bg-slate-950/40 dark:border-white/5 dark:text-emerald-400"
                  >
                    <td colSpan={4} className="px-5 py-2.5 font-sans">
                      Paid amount
                    </td>
                    <td
                      className="px-4 py-2.5 text-right font-mono"
                      colSpan={1}
                    >
                      {formatMoney(pageTotals.paidAmount)}
                    </td>
                    <td colSpan={5} />
                  </tr>

                  {/* Withhold (TWHT) */}
                  {pageTotals.withhold > 0 && (
                    <tr
                      className="text-xs font-semibold
      bg-slate-50 border-t border-slate-100 text-amber-700
      dark:bg-slate-950/40 dark:border-white/5 dark:text-amber-400"
                    >
                      <td colSpan={4} className="px-5 py-2.5 font-sans">
                        Income Withhold (TWTH)
                      </td>
                      <td
                        className="px-4 py-2.5 text-right font-mono"
                        colSpan={1}
                      >
                        {formatMoney(pageTotals.withhold)}
                      </td>
                      <td colSpan={5} />
                    </tr>
                  )}

                  {/* VAT withhold */}
                  {pageTotals.vatWithhold > 0 && (
                    <tr
                      className="text-xs font-semibold
      bg-slate-50 border-t border-slate-100 text-amber-700
      dark:bg-slate-950/40 dark:border-white/5 dark:text-amber-400"
                    >
                      <td colSpan={4} className="px-5 py-2.5 font-sans">
                        VAT withhold (VWTH)
                      </td>
                      <td
                        className="px-4 py-2.5 text-right font-mono"
                        colSpan={1}
                      >
                        {formatMoney(pageTotals.vatWithhold)}
                      </td>
                      <td colSpan={5} />
                    </tr>
                  )}

                  {/* Balance */}
                  <tr
                    className="text-xs font-bold
      bg-slate-100 border-t border-slate-200 text-slate-900
      dark:bg-slate-950/70 dark:border-white/10 dark:text-white"
                  >
                    <td colSpan={4} className="px-5 py-2.5 font-sans">
                      Unpaid amount
                    </td>
                    <td
                      className="px-4 py-2.5 text-right font-mono"
                      colSpan={1}
                    >
                      {formatMoney(pageTotals.balanceAmount)}
                    </td>
                    <td colSpan={5} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Pagination – with page-size selector */}
          {(totalPages > 1 || totalElements > 0) && (
            <div
              className="px-6 py-4 border-t flex flex-col sm:flex-row items-center justify-between gap-3 text-xs
    bg-slate-50 border-slate-200 text-slate-500
    dark:bg-slate-950/40 dark:border-white/5 dark:text-slate-400"
            >
              {/* Left: showing X of Y + size selector */}
              <div className="flex items-center gap-3 flex-wrap">
                <div>
                  Showing{" "}
                  <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                    {invoices.length}
                  </span>{" "}
                  of{" "}
                  <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                    {totalElements}
                  </span>{" "}
                  records.
                </div>

                {/* Page size selector */}
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 dark:text-slate-400">
                    Rows per page:
                  </span>
                  <select
                    value={size}
                    onChange={(e) => setSize(Number(e.target.value))}
                    disabled={isLoading}
                    className="rounded-lg px-2.5 py-1.5 text-xs font-semibold border appearance-none focus:outline-none focus:border-amber-500/50
            bg-white border-slate-200 text-slate-800
            dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200
            disabled:opacity-50"
                  >
                    {PAGE_SIZE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Right: prev / page indicator / next */}
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    disabled={page === 0 || isLoading}
                    onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                    className="p-2 rounded-md border transition disabled:opacity-30 disabled:pointer-events-none
            bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100
            dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    <FaChevronLeft />
                  </button>
                  <span className="font-mono text-slate-700 dark:text-slate-200">
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    disabled={page >= totalPages - 1 || isLoading}
                    onClick={() => setPage((prev) => prev + 1)}
                    className="p-2 rounded-md border transition disabled:opacity-30 disabled:pointer-events-none
            bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100
            dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    <FaChevronRight />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Contextual Menu */}
      {activeMenuId !== null && activeInvoice && (
        <div
          ref={menuContainerRef}
          className="fixed w-52 rounded-xl shadow-2xl p-1.5 z-[999] text-left backdrop-blur-xl space-y-0.5 border
            bg-white border-slate-200
            dark:bg-slate-900 dark:border-white/10 dark:bg-opacity-95"
          style={{ top: menuAnchor.top, left: menuAnchor.left }}
        >
          <button
            type="button"
            onClick={() => handleOpenActionModal(activeInvoice, "process")}
            className={menuButtonClass}
          >
            <FaEye className="text-slate-400 text-sm" />
            View details
          </button>
          {workflow.canPay && (
            <button
              type="button"
              onClick={() => handleOpenActionModal(activeInvoice, "payment")}
              className={menuButtonClass}
            >
              <FaMoneyBillWave className="text-slate-400 text-sm" />
              Settle Payment
            </button>
          )}

          {workflow.canWithhold &&
            (activeInvoice.withhold > 0 || activeInvoice.vatWithhold) > 0 && (
              <button
                type="button"
                onClick={() => {
                  setOpenRegisterWithholds(true);
                  setActiveMenuId(null);
                }}
                className={menuButtonClass}
              >
                <FaMoneyBillWave className="text-slate-400 text-sm" />
                Register Withholds
              </button>
            )}
          {workflow.canCancel && (
            <button
              type="button"
              onClick={() => handleOpenActionModal(activeInvoice, "cancel")}
              className={menuButtonClass}
            >
              <FaTrash className="text-slate-400 text-sm" />
              Cancel
            </button>
          )}
          {workflow.canReturn && activeInvoice && (
            <button
              type="button"
              onClick={() => handleOpenActionModal(activeInvoice, "memo")}
              className={menuButtonClass}
            >
              <FaUndoAlt className="text-amber-500 dark:text-amber-400 text-sm" />
              {refundNote}
            </button>
          )}
          {canOpenReceipt &&
            activeInvoice &&
            activeInvoice?.type === "SALE" && (
              <button
                type="button"
                onClick={() => {
                  setReceiptInvoice(activeInvoice);
                  setOpenReceipt(true);
                  setActiveMenuId(null);
                }}
                className={menuButtonClass}
              >
                <FaReceipt className="text-slate-400 text-sm" />
                Print Invoice Receipt
              </button>
            )}

          {canOpenReceipt &&
            activeInvoice?.type === "PURCHASE" &&
            (activeInvoice.withhold > 0 || activeInvoice.vatWithhold) > 0 && (
              <button
                type="button"
                onClick={() => {
                  setReceiptInvoice(activeInvoice);
                  setReceiptWithholdingType("INCOME_TAX");
                  setOpenWhReceipt(true);
                  setActiveMenuId(null);
                }}
                className={menuButtonClass}
              >
                <FaReceipt className="text-slate-400 text-sm" />
                Print TWTH Receipt
              </button>
            )}

          {canOpenReceipt &&
            activeInvoice?.type === "PURCHASE" &&
            (activeInvoice.withhold > 0 || activeInvoice.vatWithhold) > 0 && (
              <button
                type="button"
                onClick={() => {
                  setReceiptInvoice(activeInvoice);
                  setReceiptWithholdingType("VAT");
                  setOpenWhReceipt(true);
                  setActiveMenuId(null);
                }}
                className={menuButtonClass}
              >
                <FaReceipt className="text-slate-400 text-sm" />
                Print VWTH Receipt
              </button>
            )}
        </div>
      )}

      {showExternalImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70">
          <div
            className="w-full max-w-md rounded-2xl p-6 shadow-xl border backdrop-blur-md
        bg-white border-slate-200
        dark:bg-slate-900 dark:border-white/10"
          >
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Import External Invoices
              </h2>

              <button
                type="button"
                onClick={() => setShowExternalImportModal(false)}
                className="text-xl text-slate-400 hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-200 transition"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Payment Type
                </label>
                <select
                  value={importMode}
                  onChange={(e) => setImportMode(e.target.value)}
                  className="w-full rounded-xl border px-3 py-2.5 text-sm transition focus:outline-none focus:border-amber-500/40
              bg-slate-50 border-slate-200 text-slate-800
              dark:bg-slate-950/60 dark:border-white/10 dark:text-white"
                >
                  <option value="CASH">CASH</option>
                  <option value="CREDIT">CREDIT</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Issue Date
                </label>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
                      From
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full rounded-xl border px-3 py-2.5 text-sm transition focus:outline-none focus:border-amber-500/40
                  bg-slate-50 border-slate-200 text-slate-800
                  dark:bg-slate-950/60 dark:border-white/10 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
                      To
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      min={startDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full rounded-xl border px-3 py-2.5 text-sm transition focus:outline-none focus:border-amber-500/40
                  bg-slate-50 border-slate-200 text-slate-800
                  dark:bg-slate-950/60 dark:border-white/10 dark:text-white"
                    />
                  </div>
                </div>

                {startDate && endDate && (
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Selected: {formatDate(startDate)} - {formatDate(endDate)}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowExternalImportModal(false)}
                className="rounded-xl border px-4 py-2 text-sm font-medium transition
            border-slate-200 text-slate-700 hover:bg-slate-50
            dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={!startDate || !endDate || isImporting}
                onClick={handleExternalSync}
                className="rounded-xl px-4 py-2 text-sm font-medium text-white transition
            bg-amber-600 hover:bg-amber-500
            disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isImporting ? "Importing..." : "Import"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <InvoiceModal
        isOpen={modals.create}
        invoice={null}
        initialMode="CREATE"
        invoiceType={selectedType}
        onClose={() => closeModal("create")}
        onSaveSuccess={refreshRegistryGrid}
      />

      <InvoiceModal
        isOpen={modals.process}
        invoice={activeInvoice}
        initialMode="EDIT"
        invoiceType={activeInvoice?.type || selectedType}
        onClose={() => closeModal("process")}
        onSaveSuccess={refreshRegistryGrid}
      />

      <InvoiceModal
        isOpen={modals.memo}
        invoice={activeInvoice}
        initialMode="MEMO"
        invoiceType={selectedType}
        onClose={() => closeModal("memo")}
        onSaveSuccess={refreshRegistryGrid}
      />

      <InvoicePaymentModal
        isOpen={modals.payment}
        invoice={activeInvoice}
        onClose={() => closeModal("payment")}
        onSuccess={refreshRegistryGrid}
      />

      <InvoiceCancelModal
        isOpen={modals.cancel}
        invoice={activeInvoice}
        onClose={() => {
          closeModal("cancel");
        }}
        onCancelSuccess={(updatedInvoice) => {
          setInvoices((prev) =>
            prev.map((invoice) =>
              invoice.id === updatedInvoice.id ? updatedInvoice : invoice
            )
          );

          setActiveInvoice(null);
        }}
      />

      <InvoiceReceipt
        invoiceId={receiptInvoice?.id}
        isOpen={openReceipt}
        onClose={() => {
          setOpenReceipt(false);
          setReceiptInvoice(null);
        }}
        type="SALE"
      />

      <WithholdingReceipt
        isOpen={openWhReceipt}
        onClose={() => {
          setOpenWhReceipt(false);
          setReceiptInvoice(null);
          setReceiptWithholdingType(null);
        }}
        invoiceId={activeInvoice?.id}
        type={receiptWithholdingType}
      />

      <AuditLogModal
        isOpen={openAudit}
        onClose={() => {
          setOpenAudit(false);
          setSelectedEntityId(null);
        }}
        entityId={selectedEntityId}
        title="Audit History"
      />

      {showOpenModal && (
        <PosOpenModal
          openingCash={openingCash}
          setOpeningCash={setOpeningCash}
          onSubmit={handleOpenShift}
          isSubmitting={isSubmittingShift}
          shiftError={shiftError}
        />
      )}
      {showCloseModal && (
        <PosCloseModal
          countedCash={countedCash}
          setCountedCash={setCountedCash}
          onClose={() => setShowCloseModal(false)}
          onSubmit={handleCloseShift}
          isSubmitting={isSubmittingShift}
          shiftError={shiftError}
        />
      )}

      <RegisterWithholds
        isOpen={openRegisterWithholds}
        invoice={activeInvoice}
        onClose={() => setOpenRegisterWithholds(false)}
      />
    </div>
  );
};

export default InvoicesPage;
